/**
 * Catalogo CER (Elenco europeo dei rifiuti) — lookup e normalizzazione.
 *
 * Sorgente: src/data/cer-catalog.json (asset statico versionato, generato da
 * EUR-Lex via e2e/scripts/generate-cer-catalog.mjs — vedi _meta nel JSON).
 * La dicitura ufficiale NON si salva a DB: si deriva a runtime dal codice.
 * Il confronto normalizza gli spazi (e l'eventuale asterisco) da entrambi i
 * lati, così anche i dati legacy ("16 02 13*") matchano la voce a catalogo.
 */
import catalog from '../data/cer-catalog.json';

export interface CerCatalogEntry {
  code: string;        // "XX XX XX"
  description: string; // dicitura ufficiale IT
  hazardous: boolean;  // voce con asterisco nell'elenco ufficiale
  chapter: string;     // "XX"
}

export const CER_CATALOG: CerCatalogEntry[] = catalog.entries;

// Indice per cifre (chiave senza spazi/asterischi): lookup O(1).
const byDigits = new Map<string, CerCatalogEntry>(
  CER_CATALOG.map((e) => [e.code.replace(/\D/g, ''), e]),
);

/** Solo le cifre di un input qualsiasi ("16 02 13*" → "160213"). */
export function cerDigits(input: string): string {
  return input.replace(/\D/g, '');
}

/**
 * Formattazione progressiva dell'input codice: tiene le prime 6 cifre e le
 * raggruppa a 2 ("160214" → "16 02 14", "1602" → "16 02"). Input con lettere
 * (ricerca per testo) viene lasciato intatto.
 */
export function formatCerInput(raw: string): string {
  if (/[^\d\s]/.test(raw)) return raw;
  const digits = cerDigits(raw).slice(0, 6);
  const groups = digits.match(/\d{1,2}/g) ?? [];
  return groups.join(' ');
}

/** Codice valido = esattamente "XX XX XX". */
export function isValidCerCode(code: string): boolean {
  return /^\d{2} \d{2} \d{2}$/.test(code);
}

/** Normalizza a "XX XX XX" se l'input contiene 6 cifre, altrimenti null. */
export function normalizeCerCode(input: string): string | null {
  const digits = cerDigits(input);
  if (digits.length !== 6) return null;
  return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)}`;
}

/** Voce a catalogo per un codice in qualunque formato (spazi/asterisco ignorati). */
export function findCatalogEntry(code: string): CerCatalogEntry | null {
  const digits = cerDigits(code);
  if (digits.length !== 6) return null;
  return byDigits.get(digits) ?? null;
}

/**
 * Resa a video/stampa di un codice CER: l'asterisco dei pericolosi è DERIVATO
 * da `classificazione` (post-V50 il DB ha sole cifre "XX XX XX"). Helper unico
 * per ogni punto in cui il codice viene mostrato accanto alla classificazione.
 */
export function formatCer(cer: string, classificazione?: string | null): string {
  const code = cer.trim();
  if (!code) return code;
  if (classificazione === 'pericoloso' && !code.endsWith('*')) return `${code}*`;
  return code;
}

/**
 * Ricerca nel catalogo: per codice (prefisso sulle cifre — accetta "160214",
 * "16 02 14", parziali come "16 02") oppure per testo della dicitura
 * (case-insensitive, tutte le parole devono comparire).
 */
export function searchCatalog(query: string, limit = 6): CerCatalogEntry[] {
  const q = query.trim();
  if (!q) return [];
  const digits = cerDigits(q);
  // Query "da codice": solo cifre/spazi/asterischi.
  if (digits.length > 0 && !/[^\d\s*]/.test(q)) {
    return CER_CATALOG.filter((e) => e.code.replace(/\D/g, '').startsWith(digits)).slice(0, limit);
  }
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  return CER_CATALOG.filter((e) => {
    const text = e.description.toLowerCase();
    return words.every((w) => text.includes(w));
  }).slice(0, limit);
}
